import { Button, Flex } from '@mantine/core';
import { generatePath, Link } from 'react-router-dom';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ShowHideColumnsButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ShowHideColumnsButton/ShowHideColumnsButton';
import { ToggleFullScreenButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ToggleFullScreenButton/ToggleFullScreenButton';
import { TableToolbarButtonProps } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { CardTableViewSelector } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.tsx';
import { CardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.types.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { buildTableFiltersSearch } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { InvestorHoldingRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.types.ts';
import { HoldingsCardActionsStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCard.styles.tsx';

interface Props {
	table?: TableToolbarButtonProps<InvestorHoldingRow>['table'];
	dealId: string;
	singleIssuerId: string | null;
	view: CardTableView;
	onViewChange: (view: CardTableView) => void;
	isFullScreen?: boolean;
	holderFilterName?: string;
}

export const HoldingsCardActions = ({
	table,
	dealId,
	singleIssuerId,
	view,
	onViewChange,
	isFullScreen = false,
	holderFilterName,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	if (isFullScreen && table) {
		return (
			<HoldingsCardActionsStyles>
				<Flex justify="flex-end" align="center" gap="0.25rem" p="0.75rem">
					<ShowHideColumnsButton
						table={table}
						leftSection={<Icon variant="settings" size="lg" />}
						className="columns"
					/>

					<ToggleFullScreenButton table={table} className="fullscreen" iconSize="vlg" />
				</Flex>
			</HoldingsCardActionsStyles>
		);
	}

	const ownershipPath = singleIssuerId
		? generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.OWNERSHIP}`, {
				dealId,
				personId: singleIssuerId,
			})
		: null;

	const holderFilterSearch = holderFilterName
		? `?${buildTableFiltersSearch([
				{ id: 'holder', value: holderFilterName },
				{ id: 'holderName', value: holderFilterName },
			])}`
		: '';
	const to = `${ownershipPath}${holderFilterSearch}`;

	return (
		<HoldingsCardActionsStyles>
			<Flex justify="flex-end" align="center" gap="0.25rem">
				{!!ownershipPath && (
					<Button
						component={Link}
						to={to}
						variant="default"
						size="md"
						color="dark.8"
						data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_OPEN_CAPTABLE}
					>
						{tMipManage('investors.details.holdings_table.open_captable')}
					</Button>
				)}

				{!!table && (
					<ShowHideColumnsButton
						table={table}
						leftSection={<Icon variant="settings" size="lg" />}
						className="columns"
					/>
				)}

				<CardTableViewSelector
					value={view}
					onViewChange={onViewChange}
					dataTestId={
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDINGS_VIEW_SELECTOR
					}
				/>

				{!!table && (
					<ToggleFullScreenButton table={table} className="fullscreen" iconSize="vlg" />
				)}
			</Flex>
		</HoldingsCardActionsStyles>
	);
};
